import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { cn } from '@mlm/ui/lib/cn';
import {
  COACHING_WEEKLY_REVIEW_QUESTIONS,
  type CoachingWeeklyReviewKey,
} from '@mlm/shared/coaching';
import { linkButton } from '../components/panels/today';
import {
  CoachButton,
  ErrorNote,
  Eyebrow,
  Field,
  inputClass,
  Loading,
  SageCheck,
} from '../components/primitives';
import {
  coaching,
  type WeeklyContext,
  type WeeklyReview,
  type WeeklyReviewState,
} from '../lib/api';
import { longDay, shortDay } from '../lib/format';
import { qk, useRefreshCoaching } from '../lib/queries';
import { ToolFrame, toolHeading } from './tool-frame';

const REQUIRED: CoachingWeeklyReviewKey[] = ['biggestWin', 'nextWeek'];
const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
const weekLabel = (c: WeeklyContext) => `${shortDay(c.weekStart)} to ${shortDay(c.weekEnd)}`;

/**
 * SC-39: close the week. The week's own numbers sit beside five questions, and
 * the answers come back as wins, a pattern, next week's focus and three
 * priorities.
 */
export function WeeklyReviewPage() {
  const [week, setWeek] = useState<string | null>(null);
  const state = useQuery({
    queryKey: qk.weeklyReview(week),
    queryFn: () => coaching.weeklyReview(week ?? undefined),
  });
  const [result, setResult] = useState<WeeklyReview | null>(null);
  // Same reason as the evening review: a tall form is replaced by a short
  // result, and the window keeps its scroll offset.
  useEffect(() => {
    if (result) window.scrollTo({ top: 0 });
  }, [result]);

  if (state.isPending) return <Loading />;
  if (state.isError) return <ErrorNote error={state.error} retry={() => void state.refetch()} />;
  const data = state.data;
  const review = result ?? data.review;

  return (
    <ToolFrame close="/achieve">
      {review ? (
        <ReviewResult review={review} context={data.context} fresh={Boolean(result)} />
      ) : (
        <ReviewForm
          key={data.weekStart}
          state={data}
          onSwitchWeek={setWeek}
          onDone={(r) => setResult(r)}
        />
      )}
    </ToolFrame>
  );
}

function WeekFacts({ context }: { context: WeeklyContext }) {
  const rows: Array<[string, string]> = [
    ['Actions completed', `${context.actions.done} of ${context.actions.total}`],
    [
      'Average Success Score',
      context.averageScore === null ? 'No scored days yet' : String(context.averageScore),
    ],
    ['Morning rituals', days(context.morningRituals)],
    ['Evening reviews', days(context.eveningReviews)],
    ['369 practice', days(context.threeSixNineDays)],
    ['Current streak', days(context.streak)],
  ];
  return (
    <dl className="mt-8 border-t border-border">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="flex items-baseline justify-between gap-4 border-b border-border py-3"
        >
          <dt className="eyebrow">{label}</dt>
          <dd className="text-[0.95rem] text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ClosingPanel() {
  return (
    <div className="flex flex-1 flex-col justify-center" role="status" aria-live="polite">
      <Eyebrow className="mb-3">Weekly review</Eyebrow>
      <h1 className={toolHeading}>Closing your week</h1>
      <p className="mt-3 text-[0.95rem] leading-relaxed text-ink/70 lg:text-base">
        Master Kala is turning your answers into next week's focus. This takes a few seconds.
      </p>
    </div>
  );
}

function ReviewForm({
  state,
  onSwitchWeek,
  onDone,
}: {
  state: WeeklyReviewState;
  onSwitchWeek: (weekStart: string) => void;
  onDone: (review: WeeklyReview) => void;
}) {
  const refresh = useRefreshCoaching();
  const { context } = state;
  const [answers, setAnswers] = useState<Record<CoachingWeeklyReviewKey, string>>({
    biggestWin: '',
    progressSource: '',
    heldBack: '',
    learned: '',
    nextWeek: '',
  });
  const ready = REQUIRED.every((k) => answers[k].trim().length > 0);
  const lastWeek = state.weekStart !== state.currentWeekStart;

  const submit = useMutation({
    onMutate: () => window.scrollTo({ top: 0 }),
    mutationFn: () =>
      coaching.completeWeeklyReview({
        weekStart: state.weekStart,
        answers: {
          biggestWin: answers.biggestWin.trim(),
          progressSource: answers.progressSource.trim(),
          heldBack: answers.heldBack.trim(),
          learned: answers.learned.trim(),
          nextWeek: answers.nextWeek.trim(),
        },
      }),
    onSuccess: (res) => {
      coaching.event('weekly_review_completed', { source: res.review.source });
      onDone(res.review);
      void refresh();
    },
  });

  if (submit.isPending) return <ClosingPanel />;

  return (
    <form
      className="flex flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready && !submit.isPending) submit.mutate();
      }}
    >
      <Eyebrow className="mb-3">Weekly review · {weekLabel(context)}</Eyebrow>
      <h1 className={toolHeading}>Your week in review</h1>
      <p className="mt-3 text-[0.95rem] leading-relaxed text-ink/70 lg:text-base">
        Five honest answers. They come back to you as next week's focus and three priorities.
      </p>
      {lastWeek && (
        <p className="mt-4 text-sm text-ink/70">
          You are closing last week.{' '}
          <button
            type="button"
            className="eyebrow underline underline-offset-4 hover:text-ink"
            onClick={() => onSwitchWeek(state.currentWeekStart)}
          >
            Review this week instead
          </button>
        </p>
      )}

      <WeekFacts context={context} />

      {context.wins.length > 0 && (
        <section className="mt-8">
          <Eyebrow className="mb-3">Wins you wrote this week</Eyebrow>
          <ul className="space-y-1.5">
            {context.wins.map((win, i) => (
              <li key={i} className="text-sm leading-relaxed text-ink/80">
                {win}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-9 space-y-6">
        {COACHING_WEEKLY_REVIEW_QUESTIONS.map((q) => (
          <Field
            key={q.key}
            label={q.label}
            htmlFor={`weekly-${q.key}`}
            hint={REQUIRED.includes(q.key) ? undefined : 'Optional'}
          >
            <textarea
              id={`weekly-${q.key}`}
              rows={2}
              maxLength={600}
              className={cn(inputClass, 'resize-y')}
              value={answers[q.key]}
              onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
            />
          </Field>
        ))}
      </div>

      <div className="mt-auto pt-10">
        <CoachButton
          kind="gold"
          type="submit"
          block
          className="lg:w-auto lg:px-11"
          disabled={!ready || submit.isPending}
        >
          {submit.isPending ? 'Closing your week' : 'Complete review'}
        </CoachButton>
        {!ready && (
          <p className="mt-3 text-xs text-muted-foreground">
            Answer your biggest win and what matters most next week to finish.
          </p>
        )}
      </div>
    </form>
  );
}

function ReviewResult({
  review,
  context,
  fresh,
}: {
  review: WeeklyReview;
  context: WeeklyContext;
  fresh: boolean;
}) {
  const { summary } = review;
  return (
    <div className="flex flex-1 flex-col">
      <SageCheck size={28} />
      <Eyebrow className="mb-3 mt-5">Weekly review · {weekLabel(context)}</Eyebrow>
      <h1 className={toolHeading}>{fresh ? 'Your week is closed' : 'You closed this week'}</h1>
      {review.completedAt && !fresh && (
        <p className="eyebrow mt-3">Completed {longDay(review.completedAt.slice(0, 10))}</p>
      )}
      <section className="mt-8 border-t border-border pt-6">
        <Eyebrow className="mb-2">Your wins</Eyebrow>
        <p className="text-[0.95rem] leading-relaxed text-ink/80">{summary.wins}</p>
      </section>
      <section className="mt-6 border-t border-border pt-6">
        <Eyebrow className="mb-2">Pattern</Eyebrow>
        <p className="text-[0.95rem] leading-relaxed text-ink/80">{summary.pattern}</p>
      </section>
      <section className="mt-6 border-t border-border pt-6">
        <Eyebrow className="mb-2">Next week's focus</Eyebrow>
        <p className="font-display text-[1.35rem] leading-snug text-ink">{summary.focus}</p>
      </section>
      {summary.topPriorities.length > 0 && (
        <section className="mt-6 border-t border-border pt-6">
          <Eyebrow className="mb-3">Top priorities</Eyebrow>
          <ol className="space-y-2">
            {summary.topPriorities.map((p, i) => (
              <li key={i} className="flex gap-3 text-[0.95rem] leading-snug text-ink">
                <span className="font-mono text-sm text-sage">{i + 1}</span>
                {p}
              </li>
            ))}
          </ol>
        </section>
      )}
      <div className="mt-auto flex flex-wrap gap-3 pt-10">
        <Link to="/achieve/actions" className={linkButton}>
          Plan next week's actions
        </Link>
        <Link to="/achieve" className={linkButton}>
          Back to Achieve
        </Link>
      </div>
    </div>
  );
}
